/**
 * @voltxampere/web — `<vxa-pane-landing>` (Sprint 18 Paket B B3).
 *
 * The first tab of mockup zenit-platform.html (LANDING / OPENING).
 * Hero (96px italic title + Cormorant x in brass, 44px italic
 * subtitle, EB Garamond italic lede, 2 CTA), constellation SVG
 * (voltage divider rendered as observatory diagram with meteor
 * current flow), feature grid (3-col, Roman numerals I-III), stats
 * grid (4-col live counters), manifesto.
 *
 * CTAs route programmatically via setPane:
 *   "Open simulator"  -> setPane("simulator")
 *   "Ready circuits"  -> setPane("presets")
 *
 * When prefers-reduced-motion: reduce is active, the meteor +
 * node-ring animations stop (accessibility).
 *
 * Stats live values:
 *   - 64+ components (ALL_SYMBOLS.length + canvas demo extras)
 *   - test count: SPECS sentinel from CLAUDE.md (1412 baseline;
 *     the current count rolls in via paket B — Sprint 18 Paket A
 *     ended at 2554+1, but the "1412" in the mockup honours the
 *     Sprint 9 baseline; updated per Sefim's preference)
 *   - 0 dependencies
 *   - +/-0.15 V ngspice parity (PNP CE limit)
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";

import { setPane } from "../store/router.js";
import { t } from "../i18n/index.js";

@customElement("vxa-pane-landing")
export class VxaPaneLanding extends LitElement {
  static override styles = css`
    :host {
      display: block;
      height: 100%;
      overflow-y: auto;
      overflow-x: hidden;
      background: var(--void);
      color: var(--ivory);
    }

    .landing {
      max-width: 1280px;
      margin: 0 auto;
      padding: 80px var(--s-9) 120px;
    }

    /* ─────────────────── HERO ─────────────────── */
    .hero {
      display: grid;
      grid-template-columns: 1.3fr 1fr;
      gap: 80px;
      align-items: center;
      min-height: 70vh;
      padding: 60px 0 120px;
      position: relative;
    }

    .hero-caption {
      display: flex;
      align-items: baseline;
      gap: var(--s-4);
      margin-bottom: 20px;
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.3em;
      color: var(--ivory-dim);
      text-transform: uppercase;
    }

    .hero-caption .dot {
      width: 5px;
      height: 5px;
      background: var(--brass);
      border-radius: 50%;
      box-shadow: 0 0 10px var(--brass);
    }

    .hero-caption .sep-dot {
      color: var(--brass-dim);
    }

    .hero-title {
      font-family: var(--font-display);
      font-weight: 300;
      font-size: 96px;
      line-height: 0.92;
      letter-spacing: -0.015em;
      color: var(--ivory);
      /* Sprint 26 Lane 7 R5-VIS-7 — canonical 32 (was 28, 8-pt grid). */
      margin: 0 0 32px;
    }

    .hero-title .times {
      font-style: italic;
      color: var(--brass);
      font-weight: 300;
    }

    .hero-title .sub {
      display: block;
      font-weight: 300;
      font-size: 44px;
      font-style: italic;
      color: var(--ivory-dim);
      margin-top: 12px;
      letter-spacing: -0.01em;
    }

    .hero-lede {
      max-width: 560px;
      font-size: 21px;
      line-height: 1.55;
      color: var(--ivory-dim);
      font-style: italic;
      margin-bottom: 40px;
    }

    .hero-lede em {
      color: var(--ivory);
      font-style: normal;
      font-family: var(--font-display);
      font-weight: 400;
    }

    .hero-actions {
      display: flex;
      gap: 14px;
      align-items: center;
    }

    .btn {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 14px 26px;
      background: transparent;
      border: 1px solid var(--brass);
      color: var(--brass);
      font-family: var(--font-mono);
      font-size: 11px;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      cursor: pointer;
      transition:
        background 200ms ease,
        color 200ms ease,
        border-color 200ms ease;
    }

    .btn:hover {
      background: var(--brass);
      color: var(--void);
    }

    .btn-primary {
      background: var(--brass);
      color: var(--void);
      font-weight: 500;
    }

    .btn-primary:hover {
      background: var(--brass-hot);
    }

    .btn-ghost {
      border-color: var(--ivory-wisp);
      color: var(--ivory-dim);
    }

    .btn-ghost:hover {
      border-color: var(--ivory);
      color: var(--ivory);
      background: transparent;
    }

    /* ─────────────────── HERO ART ─────────────────── */
    .hero-art {
      position: relative;
      aspect-ratio: 1 / 1.1;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .hero-art svg {
      width: 100%;
      height: 100%;
    }

    @media (prefers-reduced-motion: reduce) {
      .hero-art svg .pulse,
      .hero-art svg .meteor {
        animation: none !important;
      }
    }

    /* ─────────────────── SEPARATOR ─────────────────── */
    .sep-row {
      display: flex;
      align-items: center;
      gap: var(--s-6);
      padding: 60px 0 0;
    }

    .sep-line {
      flex: 1;
      height: 1px;
      background: linear-gradient(
        to right,
        transparent,
        var(--ivory-wisp) 20%,
        var(--ivory-wisp) 80%,
        transparent
      );
    }

    .sep-label {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.4em;
      color: var(--brass);
      text-transform: uppercase;
    }

    /* ─────────────────── FEATURES ─────────────────── */
    /* Sprint 23 E1 — D-23-1 hybrid (option C): 3-col -> 4-col
       (Roman IV "Open source" added). */
    .features {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 0;
      border-top: 1px solid var(--ivory-wisp);
      border-bottom: 1px solid var(--ivory-wisp);
      margin: 40px 0 100px;
    }

    .feature {
      padding: 48px 40px;
      border-right: 1px solid var(--ivory-wisp);
      position: relative;
    }

    .feature:last-child {
      border-right: none;
    }

    .feature-num {
      font-family: var(--font-display);
      font-style: italic;
      font-weight: 300;
      font-size: 48px;
      color: var(--brass);
      line-height: 1;
      margin-bottom: 16px;
    }

    .feature-title {
      font-family: var(--font-display);
      font-weight: 400;
      font-size: 26px;
      color: var(--ivory);
      margin-bottom: 12px;
      letter-spacing: -0.005em;
    }

    .feature-desc {
      font-size: 15px;
      color: var(--ivory-dim);
      line-height: 1.6;
    }

    .feature-desc em {
      color: var(--brass);
      font-style: italic;
    }

    /* ─────────────────── STATS ─────────────────── */
    .stats {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 40px;
      padding: 60px 0 100px;
      border-bottom: 1px solid var(--ivory-wisp);
    }

    .stat {
      text-align: left;
    }

    .stat-value {
      font-family: var(--font-display);
      font-weight: 300;
      font-size: 64px;
      line-height: 1;
      color: var(--ivory);
      margin-bottom: 8px;
    }

    .stat-value .unit {
      font-size: 24px;
      font-style: italic;
      color: var(--brass);
      margin-left: 4px;
    }

    .stat-label {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.25em;
      color: var(--ivory-dim);
      text-transform: uppercase;
    }

    /* ─────────────────── ORNAMENT BREAK ─────────────────── */
    .ornament-break {
      text-align: center;
      font-size: 24px;
      color: var(--brass-dim);
      letter-spacing: 1em;
      padding: 60px 0;
    }

    /* ─────────────────── MANIFESTO ─────────────────── */
    .manifesto {
      text-align: center;
      padding: 80px 60px;
      max-width: 900px;
      margin: 0 auto;
    }

    .manifesto-intro {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.3em;
      color: var(--brass);
      text-transform: uppercase;
      margin-bottom: 32px;
    }

    .manifesto-text {
      font-family: var(--font-display);
      font-weight: 300;
      font-style: italic;
      font-size: 42px;
      line-height: 1.3;
      color: var(--ivory);
      letter-spacing: -0.01em;
    }

    .manifesto-text em {
      color: var(--brass);
      font-style: italic;
    }

    .manifesto-sign {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.3em;
      color: var(--ivory-dim);
      text-transform: uppercase;
      margin-top: 40px;
    }

    /* ─────────────────── RESPONSIVE ─────────────────── */
    @media (max-width: 1024px) {
      .hero {
        grid-template-columns: 1fr;
        gap: 60px;
        padding: 40px 0 80px;
      }

      .hero-art {
        max-width: 480px;
        margin: 0 auto;
      }

      .features {
        grid-template-columns: 1fr;
      }

      .feature {
        border-right: none;
        border-bottom: 1px solid var(--ivory-wisp);
      }

      .feature:last-child {
        border-bottom: none;
      }

      .stats {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (max-width: 640px) {
      .landing {
        padding: 40px var(--s-5) 80px;
      }

      .hero-title {
        font-size: 64px;
      }

      .hero-title .sub {
        font-size: 32px;
      }

      .stats {
        grid-template-columns: 1fr 1fr;
        gap: var(--s-6);
      }

      .stat-value {
        font-size: 48px;
      }

      .manifesto-text {
        font-size: 28px;
      }
    }
  `;

  override render() {
    return html`
      <div class="landing">
        ${this.renderHero()} ${this.renderSeparator(t("landing.sep.what"))} ${this.renderFeatures()}
        ${this.renderStats()}
        <div class="ornament-break" aria-hidden="true">❦ ❧ ❦</div>
        ${this.renderManifesto()}
      </div>
    `;
  }

  private renderHero() {
    return html`
      <div class="hero">
        <div>
          <div class="hero-caption">
            <span class="dot" aria-hidden="true"></span>
            <span>${t("landing.caption.observatorium")}</span>
            <span class="sep-dot" aria-hidden="true">·</span>
            <span>MMXXVI</span>
          </div>
          <h1 class="hero-title">
            Volt<span class="times">×</span>Ampere
            <span class="sub">${t("landing.hero.subtitle")}</span>
          </h1>
          <p class="hero-lede">${this.renderLede()}</p>
          <div class="hero-actions">
            <button
              class="btn btn-primary"
              type="button"
              data-action="open-simulator"
              title=${t("landing.cta.openSimulator")}
              @click=${this._openSimulator}
            >
              ${t("landing.cta.openSimulator")}
            </button>
            <button
              class="btn btn-ghost"
              type="button"
              data-action="open-presets"
              title=${t("landing.cta.presets")}
              @click=${this._openPresets}
            >
              ${t("landing.cta.presets")}
            </button>
          </div>
        </div>
        <div class="hero-art" aria-hidden="true">${this.renderConstellation()}</div>
      </div>
    `;
  }

  private renderLede() {
    // i18n template strings carry <em>…</em> placeholders. We split
    // the localized string on the placeholders and stitch back with
    // styled <em> nodes. Markdown-style minimal inline emphasis.
    const lede = t("landing.hero.lede");
    return this.renderRichText(lede);
  }

  private renderSeparator(label: string) {
    return html`<div class="sep-row">
      <div class="sep-line" aria-hidden="true"></div>
      <div class="sep-label">${label}</div>
      <div class="sep-line" aria-hidden="true"></div>
    </div>`;
  }

  private renderFeatures() {
    const features: readonly { num: string; titleKey: string; descKey: string }[] = [
      {
        num: "I.",
        titleKey: "landing.feature.accuracy.title",
        descKey: "landing.feature.accuracy.desc",
      },
      { num: "II.", titleKey: "landing.feature.live.title", descKey: "landing.feature.live.desc" },
      {
        num: "III.",
        titleKey: "landing.feature.localized.title",
        descKey: "landing.feature.localized.desc",
      },
      // Sprint 23 E1 — Roman IV "Open source" (D-23-1 hybrid closure).
      {
        num: "IV.",
        titleKey: "landing.feature.openSource.title",
        descKey: "landing.feature.openSource.desc",
      },
    ];
    return html`
      <div class="features">
        ${features.map(
          (f) => html`
            <div class="feature">
              <div class="feature-num">${f.num}</div>
              <div class="feature-title">${t(f.titleKey as never)}</div>
              <div class="feature-desc">${this.renderRichText(t(f.descKey as never))}</div>
            </div>
          `,
        )}
      </div>
    `;
  }

  private renderStats() {
    const stats: readonly { value: string; unit?: string; labelKey: string }[] = [
      { value: "64", unit: "+", labelKey: "landing.stat.components" },
      { value: "2554", labelKey: "landing.stat.tests" },
      { value: "0", labelKey: "landing.stat.dependencies" },
      { value: "±0.15", unit: "V", labelKey: "landing.stat.parity" },
    ];
    return html`
      <div class="stats">
        ${stats.map(
          (s) => html`
            <div class="stat">
              <div class="stat-value">
                ${s.value}${s.unit ? html`<span class="unit">${s.unit}</span>` : ""}
              </div>
              <div class="stat-label">${t(s.labelKey as never)}</div>
            </div>
          `,
        )}
      </div>
    `;
  }

  private renderManifesto() {
    return html`
      <div class="manifesto">
        <div class="manifesto-intro">${t("landing.manifesto.label")}</div>
        <div class="manifesto-text">${this.renderRichText(t("landing.manifesto.text"))}</div>
        <div class="manifesto-sign">${t("landing.manifesto.sign")}</div>
      </div>
    `;
  }

  /**
   * Splits a localized string on `<em>...</em>` markers and returns
   * a Lit template fragment. The split is plain string-based — no
   * HTML parser, so the templates retain XSS safety automatically
   * via Lit's text-binding contract.
   */
  private renderRichText(text: string): unknown[] {
    const parts: unknown[] = [];
    const re = /<em>([\s\S]*?)<\/em>/g;
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
      const before = text.slice(last, match.index);
      if (before.length > 0) parts.push(before);
      parts.push(html`<em>${match[1]}</em>`);
      last = match.index + match[0].length;
    }
    const tail = text.slice(last);
    if (tail.length > 0) parts.push(tail);
    return parts.length > 0 ? parts : [text];
  }

  private renderConstellation() {
    return html`
      <svg viewBox="0 0 400 440" xmlns="http://www.w3.org/2000/svg">
        <!-- arka halkalar -->
        <circle
          cx="200"
          cy="220"
          r="180"
          fill="none"
          stroke="var(--brass-faint)"
          stroke-width="0.8"
        />
        <circle cx="200" cy="220" r="140" fill="none" stroke="var(--brass-dim)" stroke-width="1" />
        <circle cx="200" cy="220" r="88" fill="none" stroke="var(--brass-dim)" stroke-width="1" />
        <!-- 4 cardinal-direction ticks -->
        <line x1="200" y1="30" x2="200" y2="42" stroke="var(--brass)" stroke-width="1" />
        <line x1="200" y1="398" x2="200" y2="410" stroke="var(--brass)" stroke-width="1" />
        <line x1="20" y1="220" x2="32" y2="220" stroke="var(--brass)" stroke-width="1" />
        <line x1="368" y1="220" x2="380" y2="220" stroke="var(--brass)" stroke-width="1" />

        <!-- V source -->
        <circle cx="120" cy="140" r="18" fill="none" stroke="var(--ivory)" stroke-width="1.3" />
        <line x1="110" y1="135" x2="115" y2="135" stroke="var(--ivory)" stroke-width="1.3" />
        <line x1="108" y1="140" x2="118" y2="140" stroke="var(--ivory)" stroke-width="1.3" />
        <line x1="108" y1="145" x2="118" y2="145" stroke="var(--ivory)" stroke-width="1.3" />
        <text
          x="145"
          y="134"
          font-family="EB Garamond"
          font-style="italic"
          font-size="14"
          fill="var(--brass)"
        >
          V₁
        </text>
        <text
          x="145"
          y="150"
          font-family="JetBrains Mono"
          font-size="11"
          fill="var(--ivory)"
          opacity="0.7"
        >
          12V
        </text>

        <!-- wire V -> R1 -->
        <line
          x1="120"
          y1="158"
          x2="120"
          y2="200"
          stroke="var(--ivory)"
          stroke-width="1.2"
          stroke-dasharray="2,3"
        />

        <!-- R1 zigzag -->
        <path
          d="M 120 200 L 115 206 L 125 212 L 115 218 L 125 224 L 115 230 L 125 236 L 120 242"
          fill="none"
          stroke="var(--ivory)"
          stroke-width="1.3"
          stroke-linejoin="round"
        />
        <text
          x="140"
          y="224"
          font-family="EB Garamond"
          font-style="italic"
          font-size="14"
          fill="var(--brass)"
        >
          R₁
        </text>
        <text
          x="140"
          y="240"
          font-family="JetBrains Mono"
          font-size="11"
          fill="var(--ivory)"
          opacity="0.7"
        >
          10k
        </text>

        <!-- node n2 — pulsing measurement point -->
        <circle class="pulse" cx="120" cy="260" r="4" fill="var(--brass-hot)">
          <animate attributeName="r" values="4;6;4" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle
          class="pulse"
          cx="120"
          cy="260"
          r="12"
          fill="none"
          stroke="var(--brass-hot)"
          stroke-width="0.5"
          opacity="0.5"
        >
          <animate attributeName="r" values="8;24;8" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite" />
        </circle>
        <text
          x="135"
          y="262"
          font-family="EB Garamond"
          font-style="italic"
          font-size="13"
          fill="var(--brass-hot)"
        >
          +3.84V
        </text>

        <!-- R2 -->
        <path
          d="M 120 280 L 115 286 L 125 292 L 115 298 L 125 304 L 115 310 L 125 316 L 120 322"
          fill="none"
          stroke="var(--ivory)"
          stroke-width="1.3"
          stroke-linejoin="round"
        />
        <text
          x="140"
          y="304"
          font-family="EB Garamond"
          font-style="italic"
          font-size="14"
          fill="var(--brass)"
        >
          R₂
        </text>
        <text
          x="140"
          y="320"
          font-family="JetBrains Mono"
          font-size="11"
          fill="var(--ivory)"
          opacity="0.7"
        >
          4.7k
        </text>

        <!-- ground -->
        <line x1="120" y1="322" x2="120" y2="350" stroke="var(--ivory)" stroke-width="1.2" />
        <line x1="105" y1="350" x2="135" y2="350" stroke="var(--ivory)" stroke-width="1.4" />
        <line x1="110" y1="356" x2="130" y2="356" stroke="var(--ivory)" stroke-width="1.2" />
        <line x1="115" y1="362" x2="125" y2="362" stroke="var(--ivory)" stroke-width="1" />

        <!-- current flow — meteor particles -->
        <circle class="meteor" r="1.8" fill="var(--brass-hot)">
          <animateMotion dur="3s" repeatCount="indefinite" path="M 120 160 L 120 350" />
          <animate attributeName="opacity" values="0;1;1;0" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle class="meteor" r="1.5" fill="var(--brass-hot)">
          <animateMotion dur="3s" begin="1s" repeatCount="indefinite" path="M 120 160 L 120 350" />
          <animate
            attributeName="opacity"
            values="0;1;1;0"
            dur="3s"
            begin="1s"
            repeatCount="indefinite"
          />
        </circle>
        <circle class="meteor" r="1.5" fill="var(--brass-hot)">
          <animateMotion dur="3s" begin="2s" repeatCount="indefinite" path="M 120 160 L 120 350" />
          <animate
            attributeName="opacity"
            values="0;1;1;0"
            dur="3s"
            begin="2s"
            repeatCount="indefinite"
          />
        </circle>

        <!-- stars -->
        <circle cx="60" cy="60" r="1" fill="var(--star-white)" />
        <circle cx="340" cy="80" r="1.2" fill="var(--star-white)" />
        <circle cx="320" cy="360" r="1" fill="var(--star-white)" />
        <circle cx="50" cy="320" r="1.1" fill="var(--star-white)" />
        <circle cx="280" cy="140" r="0.8" fill="var(--ivory)" opacity="0.7" />
        <circle cx="300" cy="280" r="0.8" fill="var(--ivory)" opacity="0.7" />
        <circle cx="80" cy="380" r="0.9" fill="var(--star-white)" />
        <circle cx="360" cy="200" r="0.7" fill="var(--ivory)" opacity="0.5" />

        <!-- koordinat etiketi -->
        <text
          x="200"
          y="22"
          text-anchor="middle"
          font-family="JetBrains Mono"
          font-size="9"
          fill="var(--brass)"
          opacity="0.8"
          letter-spacing="2"
        >
          N
        </text>
        <text
          x="200"
          y="426"
          text-anchor="middle"
          font-family="JetBrains Mono"
          font-size="9"
          fill="var(--brass)"
          opacity="0.8"
          letter-spacing="2"
        >
          S
        </text>
        <text
          x="14"
          y="225"
          text-anchor="middle"
          font-family="JetBrains Mono"
          font-size="9"
          fill="var(--brass)"
          opacity="0.8"
        >
          W
        </text>
        <text
          x="386"
          y="225"
          text-anchor="middle"
          font-family="JetBrains Mono"
          font-size="9"
          fill="var(--brass)"
          opacity="0.8"
        >
          E
        </text>
      </svg>
    `;
  }

  private readonly _openSimulator = (): void => {
    setPane("simulator");
  };

  private readonly _openPresets = (): void => {
    setPane("presets");
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-pane-landing": VxaPaneLanding;
  }
}
